import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MsalService } from '@azure/msal-angular';
import { catchError, forkJoin, map, Observable, of, shareReplay, switchMap } from 'rxjs';

import {
  BondDocumentationOption,
  BondTradeReference,
  CreateInvoiceInput,
  FrequencyOption,
  InvoiceDocument,
  InvoiceLine,
  InvoiceLineInput,
  InvoiceLinkType,
  InvoiceLinkTypeCode,
  InvoiceRelatedRecord,
  InvoiceRelatedRecordsPage,
  InvoiceRelatedRecordsQuery,
  InvoiceRecord,
  InvoiceSummary,
  InvoiceTypeOption,
  InvoiceWarning,
  LinkTypeOption,
  SaveWithDocumentResult,
  SendInvoiceApprovalEmailInput,
  UpsertVendorInput,
  Vendor,
  VendorDocument,
} from './invoices.types';
import { apiUrl } from '../../auth/api-url';
import { describeDocumentUploadError } from '../shared/document-upload';

@Injectable({ providedIn: 'root' })
export class InvoicesService {
  private readonly vendorEndpoint = apiUrl('Vendor');
  private readonly invoiceEndpoint = apiUrl('Invoice');
  private readonly invoiceConceptEndpoint = apiUrl('InvoiceConcept');
  private readonly invoiceReferenceEndpoint = apiUrl('InvoiceReference');
  private readonly bondDocumentationEndpoint = apiUrl('BondDocumentation');
  private readonly bondEndpoint = apiUrl('bond');

  private invoiceConceptOptions$?: Observable<InvoiceTypeOption[]>;
  private frequencyOptions$?: Observable<FrequencyOption[]>;
  private linkTypeOptions$?: Observable<LinkTypeOption[]>;
  private bondDocumentationOptions$?: Observable<BondDocumentationOption[]>;

  constructor(
    private readonly http: HttpClient,
    private readonly msalService: MsalService,
  ) {}

  getInvoiceTypeOptions(): Observable<InvoiceTypeOption[]> {
    if (!this.invoiceConceptOptions$) {
      this.invoiceConceptOptions$ = this.http.get<unknown>(this.invoiceConceptEndpoint).pipe(
        map((response) => this.extractRows(response).map((row) => ({
          id: this.toNumber(this.read(row, ['id', 'ID', 'invConceptId', 'invoiceConceptId'])),
          code: this.toText(this.read(row, ['code', 'CODE'])),
          name: this.toText(this.read(row, ['name', 'NAME'])),
        })).filter((option) => option.id > 0 && option.name)),
        shareReplay(1),
      );
    }

    return this.invoiceConceptOptions$;
  }

  /** Drops the cached concept catalog so the next read reflects catalog CRUD changes. */
  invalidateInvoiceConceptOptions(): void {
    this.invoiceConceptOptions$ = undefined;
  }

  getFrequencyOptions(): Observable<FrequencyOption[]> {
    if (!this.frequencyOptions$) {
      this.frequencyOptions$ = this.http.get<unknown>(`${this.invoiceReferenceEndpoint}/frequencies`).pipe(
        map((response) => this.normalizeReferenceOptions(response)),
        map((items) => items.map((item) => ({
          code: item.code,
          name: item.name || this.frequencyNameFromCode(item.code),
        }))),
        shareReplay(1),
      );
    }

    return this.frequencyOptions$;
  }

  getLinkTypeOptions(): Observable<LinkTypeOption[]> {
    if (!this.linkTypeOptions$) {
      this.linkTypeOptions$ = this.http.get<unknown>(`${this.invoiceReferenceEndpoint}/link-types`).pipe(
        map((response) => this.normalizeReferenceOptions(response)),
        map((items) => items.map((item) => ({
          code: item.code,
          name: item.name || this.linkTypeNameFromCode(item.code),
          uiType: this.toUiLinkType(item.code),
        }))),
        shareReplay(1),
      );
    }

    return this.linkTypeOptions$;
  }

  getBondDocumentationOptions(): Observable<BondDocumentationOption[]> {
    if (!this.bondDocumentationOptions$) {
      this.bondDocumentationOptions$ = this.http.get<unknown>(this.bondDocumentationEndpoint).pipe(
        map((response) => this.extractRows(response).map((row) => ({
          id: this.toNumber(this.read(row, ['documentationId', 'id', 'DOCUMENTATION_ID'])),
          name: this.toText(this.read(row, ['name', 'NAME'])),
          description: this.toText(this.read(row, ['description', 'DESCRIPTION'])),
        })).filter((item) => item.id > 0)),
        shareReplay(1),
      );
    }

    return this.bondDocumentationOptions$;
  }

  getVendors(searchTerm = '', vendorTypeId?: number | null): Observable<Vendor[]> {
    let params = new HttpParams();
    if (this.normalizeText(searchTerm)) {
      params = params.set('q', this.normalizeText(searchTerm));
    }
    if (typeof vendorTypeId === 'number' && vendorTypeId > 0) {
      params = params.set('vendorTypeId', String(vendorTypeId));
    }

    return this.http.get<unknown>(this.vendorEndpoint, { params }).pipe(
      map((response) => this.extractRows(response)),
      map((rows) => rows.map((row) => this.normalizeVendorRow(row))),
      switchMap((vendors) => this.enrichVendors(vendors)),
      map((vendors) => vendors.sort((left, right) => left.name.localeCompare(right.name))),
    );
  }

  getVendorOptions(): Observable<Vendor[]> {
    return this.http.get<unknown>(this.vendorEndpoint).pipe(
      map((response) => this.extractRows(response).map((row) => this.normalizeVendorRow(row))),
      map((vendors) => vendors.sort((left, right) => left.name.localeCompare(right.name))),
    );
  }

  getVendorOptionById(vendorId: number): Observable<Vendor | undefined> {
    return this.http.get<unknown>(`${this.vendorEndpoint}/${vendorId}`).pipe(
      map((response) => this.normalizeVendorRow(this.toRecord(response))),
      map((vendor) => vendor.id > 0 ? vendor : undefined),
      catchError(() => of(undefined)),
    );
  }

  getVendorInvoiceConceptIds(vendorId: number): Observable<number[]> {
    return this.getVendorConceptRows(vendorId).pipe(
      map((rows) => rows.map((row) => row.invConceptId).filter((id) => id > 0)),
    );
  }

  getVendorById(vendorId: number): Observable<Vendor | undefined> {
    const baseVendor$ = this.http.get<unknown>(`${this.vendorEndpoint}/${vendorId}`).pipe(
      map((response) => this.normalizeVendorRow(this.toRecord(response))),
    );
    const concepts$ = this.getVendorConceptRows(vendorId);
    const documents$ = this.getVendorDocuments(vendorId);
    const conceptCatalog$ = this.getInvoiceTypeOptions();

    return forkJoin({
      vendor: baseVendor$,
      conceptRows: concepts$,
      documents: documents$,
      conceptCatalog: conceptCatalog$,
    }).pipe(
      map(({ vendor, conceptRows, documents, conceptCatalog }) => {
        if (vendor.id <= 0) {
          return undefined;
        }

        const byId = new Map<number, string>(conceptCatalog.map((concept) => [concept.id, concept.name]));
        const conceptIds = conceptRows.map((item) => item.invConceptId).filter((id) => id > 0);
        const conceptNames = conceptIds
          .map((id) => byId.get(id) ?? conceptRows.find((row) => row.invConceptId === id)?.name ?? '')
          .filter((name) => !!name);

        return {
          ...vendor,
          emittedInvoiceConceptIds: conceptIds,
          emittedInvoiceTypes: conceptNames,
          documents,
        };
      }),
      catchError(() => of(undefined)),
    );
  }

  createVendor(payload: UpsertVendorInput): Observable<SaveWithDocumentResult<number>> {
    const body = {
      ...this.buildVendorBody(payload),
      createdBy: this.resolveModifiedBy(),
    };

    return this.http.post<unknown>(this.vendorEndpoint, body).pipe(
      map((response) => this.extractCreatedId(response)),
      switchMap((vendorId) => {
        if (vendorId <= 0) {
          return of({ result: 0, documentError: null });
        }

        return this.replaceVendorConcepts(vendorId, payload.emittedInvoiceConceptIds).pipe(
          switchMap(() => this.maybeCreateVendorDocument(vendorId, payload.newDocument)),
          map((documentError) => ({ result: vendorId, documentError })),
        );
      }),
      catchError(() => of({ result: 0, documentError: null })),
    );
  }

  updateVendor(vendorId: number, payload: UpsertVendorInput): Observable<SaveWithDocumentResult<boolean>> {
    const body = {
      ...this.buildVendorBody(payload),
      modifiedBy: this.resolveModifiedBy(),
    };

    return this.http.put<void>(`${this.vendorEndpoint}/${vendorId}`, body).pipe(
      switchMap(() => this.replaceVendorConcepts(vendorId, payload.emittedInvoiceConceptIds)),
      switchMap(() => this.maybeCreateVendorDocument(vendorId, payload.newDocument)),
      map((documentError) => ({ result: true, documentError })),
      catchError(() => of({ result: false, documentError: null })),
    );
  }

  deleteVendor(vendorId: number): Observable<boolean> {
    return this.http.delete<void>(`${this.vendorEndpoint}/${vendorId}`).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  getInvoices(filters?: { vendorId?: number | null; linkType?: InvoiceLinkType | null; searchTerm?: string }): Observable<InvoiceSummary[]> {
    let params = new HttpParams();
    const query = this.normalizeText(filters?.searchTerm ?? '');
    if (query) {
      params = params.set('q', query);
    }

    if (typeof filters?.vendorId === 'number' && filters.vendorId > 0) {
      params = params.set('vendorId', String(filters.vendorId));
    }

    if (filters?.linkType) {
      params = params.set('linkTypeCode', this.toApiLinkTypeCode(filters.linkType));
    }

    return this.http.get<unknown>(this.invoiceEndpoint, { params }).pipe(
      map((response) => this.extractRows(response)),
      map((rows) => rows.map((row) => this.normalizeInvoiceSummaryRow(row))),
      map((rows) => rows.sort((left, right) => this.toEpoch(right.createdAt) - this.toEpoch(left.createdAt))),
      catchError(() => of([])),
    );
  }

  getInvoicesByVendor(vendorId: number): Observable<InvoiceSummary[]> {
    return this.http.get<unknown>(`${this.vendorEndpoint}/${vendorId}/invoices`).pipe(
      map((response) => this.extractRows(response)),
      switchMap((rows) => {
        const fallbackById = this.buildInvoiceFallbackMap(rows);
        const ids = rows.map((row) => this.extractInvoiceId(row)).filter((id) => id > 0);
        if (ids.length === 0) {
          return of([]);
        }

        return forkJoin(ids.map((id) => this.getInvoiceById(id))).pipe(
          map((items) => items
            .filter((item): item is InvoiceSummary => !!item)
            .map((item) => this.applyInvoiceFallback(item, fallbackById.get(item.id))),
          ),
        );
      }),
      map((rows) => rows.sort((left, right) => this.toEpoch(right.createdAt) - this.toEpoch(left.createdAt))),
      catchError(() => of([])),
    );
  }

  getInvoiceById(invoiceId: number): Observable<InvoiceSummary | undefined> {
    return this.http.get<unknown>(`${this.invoiceEndpoint}/${invoiceId}`).pipe(
      map((header) => {
        const headerRecord = this.toRecord(header);
        const headerSources = [
          headerRecord,
          this.toRecord(headerRecord['data']),
          this.toRecord(headerRecord['item']),
          this.toRecord(headerRecord['invoice']),
          this.toRecord(headerRecord['header']),
          this.toRecord(headerRecord['result']),
        ];
        const lineRows = this.extractRows(this.readFromSources(headerSources, ['lines']));
        const normalizedLines = lineRows.map((row) => this.normalizeLineRow(row, new Map(), new Map()));

        const conceptIds = normalizedLines
          .map((line) => line.invConceptId)
          .filter((conceptId) => conceptId > 0)
          .filter((conceptId, index, all) => all.indexOf(conceptId) === index);
        const conceptNames = normalizedLines
          .map((line) => line.conceptName)
          .filter((name) => !!name)
          .filter((name, index, all) => all.indexOf(name) === index);

        const linkTypeCode = this.toLinkTypeCode(this.toText(this.readFromSources(headerSources, ['linkTypeCode', 'LINK_TYPE_CODE'])));
        // The header no longer carries a reference; programs now live on the lines.
        const programLine = normalizedLines.find((line) => line.bondDocumentationId !== null);

        const normalized: InvoiceRecord = {
          id: this.toNumber(this.readFromSources(headerSources, ['id', 'ID'])),
          vendorId: this.toNumber(this.readFromSources(headerSources, ['vendorId', 'VENDOR_ID'])),
          vendorName: this.toText(this.readFromSources(headerSources, ['vendorName', 'VENDOR_NAME'])),
          issueDate: this.toText(this.readFromSources(headerSources, ['issueDate', 'ISSUE_DATE'])),
          invoiceConceptIds: conceptIds,
          invoiceConceptNames: conceptNames,
          linkType: this.toUiLinkType(linkTypeCode),
          linkTypeCode,
          linkedProgram: programLine
            ? (programLine.bondDocumentationName || null)
            : null,
          linkedBondDocumentationId: programLine?.bondDocumentationId ?? null,
          invoiceNumber: this.toText(this.readFromSources(headerSources, ['invoiceNumber', 'INVOICE_NUMBER'])),
          serviceDateStart: this.toNullableText(this.readFromSources(headerSources, ['serviceStartDate', 'serviceDateStart', 'SERVICE_START_DATE'])),
          serviceDateEnd: this.toNullableText(this.readFromSources(headerSources, ['serviceEndDate', 'serviceDateEnd', 'SERVICE_END_DATE'])),
          currency: this.toText(this.readFromSources(headerSources, ['invoiceCcy', 'currency', 'INVOICE_CCY'])),
          frequency: this.toText(this.readFromSources(headerSources, ['invoiceFreqName', 'frequency', 'INVOICE_FREQ'])),
          frequencyCode: this.toText(this.readFromSources(headerSources, ['invoiceFreq', 'frequencyCode', 'INVOICE_FREQ'])),
          amount: this.toNumber(this.readFromSources(headerSources, ['totalAmount', 'amount', 'TOTAL_AMOUNT'])),
          createdAt: this.toText(this.readFromSources(headerSources, ['createdDate', 'dateCreated', 'createdAt', 'DATE_CREATED', 'createdOn', 'CREATED_ON'])),
          createdBy: this.toText(this.readFromSources(headerSources, ['createdBy', 'CREATED_BY'])),
          lastModifiedBy: this.toText(this.readFromSources(headerSources, ['lastModifiedBy', 'modifiedBy', 'MODIFIED_BY'])),
          lines: normalizedLines,
          documents: this.extractRows(this.readFromSources(headerSources, ['documents']))
            .map((row) => this.normalizeInvoiceDocumentRow(row)),
          warnings: this.normalizeWarnings(this.readFromSources(headerSources, ['warnings'])),
        };

        return this.toSummary(normalized);
      }),
      catchError(() => of(undefined)),
    );
  }

  createInvoice(payload: CreateInvoiceInput): Observable<SaveWithDocumentResult<number>> {
    const headerBody = this.toInvoiceHeaderPayload(payload);

    return this.http.post<unknown>(this.invoiceEndpoint, headerBody).pipe(
      map((response) => this.extractCreatedId(response)),
      switchMap((invoiceId) => {
        if (invoiceId <= 0) {
          return of({ result: 0, documentError: null });
        }

        // Lines must land before the document so a failed replace does not orphan an upload.
        return this.replaceInvoiceLines(invoiceId, payload.lines).pipe(
          switchMap(() => this.maybeCreateInvoiceDocument(invoiceId, payload)),
          map((documentError) => ({ result: invoiceId, documentError })),
        );
      }),
      catchError(() => of({ result: 0, documentError: null })),
    );
  }

  updateInvoice(invoiceId: number, payload: CreateInvoiceInput): Observable<SaveWithDocumentResult<boolean>> {
    const headerBody = this.toInvoiceHeaderPayload(payload);

    return this.http.put<void>(`${this.invoiceEndpoint}/${invoiceId}`, headerBody).pipe(
      switchMap(() => this.replaceInvoiceLines(invoiceId, payload.lines)),
      switchMap(() => this.maybeCreateInvoiceDocument(invoiceId, payload)),
      map((documentError) => ({ result: true, documentError })),
      catchError(() => of({ result: false, documentError: null })),
    );
  }

  sendInvoiceApprovalEmail(payload: SendInvoiceApprovalEmailInput): Observable<boolean> {
    const recipients = [...new Set((payload.recipientEmail ?? '')
      .split(/[;,]/)
      .map((recipient) => recipient.trim())
      .filter((recipient) => !!recipient))];
    const requests = (recipients.length > 0 ? recipients : [null]).map((recipient) => {
      const body: Record<string, unknown> = {
        invoiceCode: payload.invoiceCode.trim(),
        messageDetail: payload.messageDetail?.trim() || null,
      };
      if (recipient) {
        body['recipientEmail'] = recipient;
      }

      return this.http.post<void>(`${this.invoiceEndpoint}/approval-email`, body);
    });

    return forkJoin(requests).pipe(
      map(() => true),
      catchError(() => of(false)),
    );
  }

  /**
   * A 409 means the invoice still has lines or documents; the payload carries the counts so the
   * UI can explain why the delete was rejected.
   */
  deleteInvoice(invoiceId: number): Observable<{ deleted: boolean; message: string }> {
    return this.http.delete<void>(`${this.invoiceEndpoint}/${invoiceId}`).pipe(
      map(() => ({ deleted: true, message: '' })),
      catchError((error: unknown) => {
        const httpError = error as { status?: number; error?: unknown };
        if (httpError?.status !== 409) {
          return of({ deleted: false, message: 'Unable to delete invoice.' });
        }

        const payload = this.toRecord(httpError.error);
        const lineCount = this.toNumber(this.read(payload, ['lineCount', 'LINE_COUNT']));
        const documentCount = this.toNumber(this.read(payload, ['documentCount', 'DOCUMENT_COUNT']));
        const parts: string[] = [];
        if (lineCount > 0) {
          parts.push(`${lineCount} line(s)`);
        }
        if (documentCount > 0) {
          parts.push(`${documentCount} document(s)`);
        }

        return of({
          deleted: false,
          message: parts.length
            ? `This invoice still has ${parts.join(' and ')}. Remove them before deleting.`
            : 'This invoice cannot be deleted while it has related records.',
        });
      }),
    );
  }

  searchBondTrades(linkType: InvoiceLinkType, searchTerm: string): Observable<BondTradeReference[]> {
    let params = new HttpParams().set('pageNumber', '1').set('pageSize', '200');
    const query = this.normalizeText(searchTerm);
    if (query) {
      params = params.set('search', query);
    }

    return this.http.get<unknown>(this.bondEndpoint, { params }).pipe(
      map((response) => {
        const record = this.toRecord(response);
        const rows = Array.isArray(record['items'])
          ? this.extractRows(record['items'])
          : this.extractRows(response);
        const mapped = rows.map((row) => this.normalizeBondTrade(row));

        if (!query) {
          return mapped;
        }

        return mapped.filter((trade) => {
          if (linkType === 'Deal ID') {
            return trade.dealId.toLowerCase().includes(query.toLowerCase());
          }

          if (linkType === 'Security ID') {
            return trade.securityId.toLowerCase().includes(query.toLowerCase());
          }

          return true;
        });
      }),
      catchError(() => of([])),
    );
  }

  getInvoiceRelatedRecords(query: InvoiceRelatedRecordsQuery): Observable<InvoiceRelatedRecordsPage> {
    let params = new HttpParams()
      .set('linkTypeCode', query.linkTypeCode)
      .set('pageNumber', String(query.pageNumber))
      .set('pageSize', String(query.pageSize));

    const search = this.normalizeText(query.search ?? '');
    const seriesNumber = this.normalizeText(query.seriesNumber ?? '');
    if (search) {
      params = params.set('search', search);
    }
    if (seriesNumber) {
      params = params.set('seriesNumber', seriesNumber);
    }
    if (query.linkTypeCode === 'BOND_DOCUMENTATION' && query.bondDocumentationId) {
      params = params.set('bondDocumentationId', String(query.bondDocumentationId));
    }

    return this.http.get<unknown>(`${this.invoiceReferenceEndpoint}/related-records`, { params }).pipe(
      map((response) => {
        const record = this.toRecord(response);
        const items = this.extractRows(record['items']).map((row) => {
          const item = this.normalizeInvoiceRelatedRecord(row, query.linkTypeCode);
          return query.linkTypeCode === 'BOND_DOCUMENTATION' && !item.bondDocumentationId
            ? { ...item, bondDocumentationId: query.bondDocumentationId ?? null }
            : item;
        });
        const pageSize = this.toNumber(this.read(record, ['pageSize', 'PAGE_SIZE'])) || query.pageSize;
        const totalRecords = this.toNumber(this.read(record, ['totalRecords', 'TOTAL_RECORDS', 'totalCount']));
        const totalPages = this.toNumber(this.read(record, ['totalPages', 'TOTAL_PAGES']))
          || (pageSize > 0 ? Math.ceil(totalRecords / pageSize) : 0);

        return {
          pageNumber: this.toNumber(this.read(record, ['pageNumber', 'PAGE_NUMBER'])) || query.pageNumber,
          pageSize,
          totalRecords,
          totalPages,
          items,
        };
      }),
    );
  }

  private enrichVendors(vendors: Vendor[]): Observable<Vendor[]> {
    if (vendors.length === 0) {
      return of([]);
    }

    const conceptCatalog$ = this.getInvoiceTypeOptions();
    const perVendor$ = vendors.map((vendor) => forkJoin({
      conceptRows: this.getVendorConceptRows(vendor.id),
      documents: this.getVendorDocuments(vendor.id),
      vendor: of(vendor),
    }));

    return forkJoin({
      conceptCatalog: conceptCatalog$,
      vendorBundles: forkJoin(perVendor$),
    }).pipe(
      map(({ conceptCatalog, vendorBundles }) => {
        const conceptById = new Map<number, string>(conceptCatalog.map((item) => [item.id, item.name]));
        return vendorBundles.map(({ vendor, conceptRows, documents }) => {
          const conceptIds = conceptRows.map((row) => row.invConceptId).filter((id) => id > 0);
          const conceptNames = conceptIds
            .map((id) => conceptById.get(id) ?? conceptRows.find((row) => row.invConceptId === id)?.name ?? '')
            .filter((name) => !!name);

          return {
            ...vendor,
            emittedInvoiceConceptIds: conceptIds,
            emittedInvoiceTypes: conceptNames,
            documents,
          };
        });
      }),
      catchError(() => of(vendors)),
    );
  }

  private getVendorConceptRows(vendorId: number): Observable<Array<{ invConceptId: number; name: string }>> {
    return this.http.get<unknown>(`${this.vendorEndpoint}/${vendorId}/concepts`).pipe(
      map((response) => this.extractRows(response).map((row) => ({
        invConceptId: this.toNumber(this.read(row, ['invConceptId', 'invoiceConceptId', 'id'])),
        name: this.toText(this.read(row, ['invConceptName', 'name'])),
      }))),
      catchError(() => of([])),
    );
  }

  private getVendorDocuments(vendorId: number): Observable<VendorDocument[]> {
    return this.http.get<unknown>(`${this.vendorEndpoint}/${vendorId}/documents`).pipe(
      map((response) => this.extractRows(response).map((row) => this.normalizeVendorDocumentRow(row))),
      catchError(() => of([])),
    );
  }

  /** Resolves to `null` on success or to a user-facing error; a failed upload never fails the vendor save. */
  private maybeCreateVendorDocument(
    vendorId: number,
    document: UpsertVendorInput['newDocument'],
  ): Observable<string | null> {
    if (!document?.file) {
      return of(null);
    }

    const fileName = document.fileName || document.file.name;
    const formData = new FormData();
    formData.append('file', document.file, fileName);
    formData.append('fileName', fileName);
    formData.append('description', document.description || '');
    formData.append('type', document.type || 'SUPPORT');
    formData.append('createdBy', this.resolveModifiedBy());

    return this.http.post<void>(`${this.vendorEndpoint}/${vendorId}/documents`, formData).pipe(
      map(() => null),
      catchError((error: unknown) => of(describeDocumentUploadError(fileName, error))),
    );
  }

  private replaceVendorConcepts(vendorId: number, conceptIds: number[]): Observable<unknown> {
    const items = conceptIds.map((conceptId, index) => ({
      invConceptId: conceptId,
      isDefault: index === 0 ? 'Y' : 'N',
    }));
    const body = {
      items,
      modifiedBy: this.resolveModifiedBy(),
    };

    return this.http.put<void>(`${this.vendorEndpoint}/${vendorId}/concepts`, body);
  }

  /**
   * Replaces every line in one call. Reference fields are nulled out according to the link
   * type because the backend rejects any combination that does not match the code.
   */
  private replaceInvoiceLines(invoiceId: number, lines: InvoiceLineInput[]): Observable<unknown> {
    const items = lines.map((line) => {
      const linkTypeCode = line.linkTypeCode;

      return {
        invConceptId: line.invConceptId,
        linkTypeCode,
        tradeId: linkTypeCode === 'NONE' ? null : (line.tradeId || null),
        dealId: linkTypeCode === 'DEAL' ? (line.dealId || null) : null,
        securityId: linkTypeCode === 'SECURITY' ? (line.securityId || null) : null,
        bondDocumentationId: linkTypeCode === 'BOND_DOCUMENTATION' ? line.bondDocumentationId : null,
        tradeCcy: line.tradeCcy || null,
        lineAmount: line.lineAmount,
        detail: line.detail || null,
      };
    });

    return this.http.put<void>(`${this.invoiceEndpoint}/${invoiceId}/lines`, {
      items,
      modifiedBy: this.resolveModifiedBy(),
    });
  }

  /** Resolves to `null` on success or to a user-facing error; a failed upload never fails the invoice save. */
  private maybeCreateInvoiceDocument(invoiceId: number, payload: CreateInvoiceInput): Observable<string | null> {
    const file = payload.attachmentFile;
    if (!file) {
      return of(null);
    }

    const fileName = payload.attachmentName || file.name;
    const formData = new FormData();
    formData.append('file', file, fileName);
    formData.append('fileName', fileName);
    formData.append('description', 'Invoice document');
    formData.append('type', this.resolveDocumentType(fileName));
    formData.append('isCurrent', 'Y');
    formData.append('createdBy', this.resolveModifiedBy());

    return this.http.post<void>(`${this.invoiceEndpoint}/${invoiceId}/documents`, formData).pipe(
      map(() => null),
      catchError((error: unknown) => of(describeDocumentUploadError(fileName, error))),
    );
  }

  /** Documents are not always PDFs, so the type comes from the uploaded file extension. */
  private resolveDocumentType(fileName: string): string {
    const normalized = fileName.trim();
    const extensionIndex = normalized.lastIndexOf('.');
    if (extensionIndex <= 0 || extensionIndex === normalized.length - 1) {
      return 'FILE';
    }

    return normalized.slice(extensionIndex + 1).toUpperCase();
  }

  private toInvoiceHeaderPayload(payload: CreateInvoiceInput): Record<string, unknown> {
    // The header only keeps the link type as a mode; references now live on each line.
    return {
      invoiceNumber: payload.invoiceNumber,
      vendorId: payload.vendorId,
      linkTypeCode: this.toApiLinkTypeCode(payload.linkType),
      issueDate: payload.issueDate,
      serviceStartDate: payload.serviceDateStart,
      serviceEndDate: payload.serviceDateEnd,
      invoiceCcy: payload.currency,
      invoiceFreq: payload.frequencyCode || null,
      totalAmount: payload.amount,
    };
  }

  private normalizeVendorRow(row: Record<string, unknown>): Vendor {
    return {
      id: this.toNumber(this.read(row, ['id', 'ID'])),
      name: this.toText(this.read(row, ['name', 'NAME'])),
      vendorTypeId: this.toNullableNumber(this.read(row, ['vendorTypeId', 'VENDOR_TYPE_ID'])),
      vendorTypeCode: this.toText(this.read(row, ['vendorTypeCode', 'VENDOR_TYPE_CODE'])),
      vendorTypeName: this.toText(this.read(row, ['vendorTypeName', 'VENDOR_TYPE_NAME'])),
      description: this.toText(this.read(row, ['description', 'DESCRIPTION'])),
      defaultCurrency: this.toText(this.read(row, ['defaultCcy', 'defaultCurrency', 'DEFAULT_CCY'])),
      addressLine1: this.toText(this.read(row, ['addressLine1', 'ADDRESS_LINE1', 'ADDRESS_LINE_1'])),
      addressLine2: this.toText(this.read(row, ['addressLine2', 'ADDRESS_LINE2', 'ADDRESS_LINE_2'])),
      city: this.toText(this.read(row, ['city', 'CITY'])),
      stateProvince: this.toText(this.read(row, ['stateProvince', 'STATE_PROVINCE'])),
      postalCode: this.toText(this.read(row, ['postalCode', 'POSTAL_CODE'])),
      countryCode: this.toText(this.read(row, ['countryCode', 'COUNTRY_CODE'])),
      phoneNumber: this.toText(this.read(row, ['phoneNumber', 'PHONE_NUMBER'])),
      invoiceCount: this.toNumber(this.read(row, ['invoiceCount', 'INVOICE_COUNT'])),
      latestInvoiceDate: this.toText(this.read(row, ['latestInvoiceDate', 'LATEST_INVOICE_DATE'])),
      emittedInvoiceTypes: [],
      emittedInvoiceConceptIds: [],
      documents: [],
    };
  }

  private buildVendorBody(payload: UpsertVendorInput): Record<string, unknown> {
    return {
      name: payload.name,
      vendorTypeId: payload.vendorTypeId,
      description: payload.description || null,
      defaultCcy: payload.defaultCurrency,
      addressLine1: payload.addressLine1 || null,
      addressLine2: payload.addressLine2 || null,
      city: payload.city || null,
      stateProvince: payload.stateProvince || null,
      postalCode: payload.postalCode || null,
      countryCode: payload.countryCode || null,
      phoneNumber: payload.phoneNumber || null,
    };
  }

  private normalizeVendorDocumentRow(row: Record<string, unknown>): VendorDocument {
    return {
      id: this.toNumber(this.read(row, ['id', 'ID'])),
      description: this.toText(this.read(row, ['description', 'DESCRIPTION'])),
      type: this.toText(this.read(row, ['type', 'TYPE'])),
      fileName: this.toText(this.read(row, ['fileName', 'FILE_NAME'])),
      ezshareLink: this.toText(this.read(row, ['ezshareLink', 'ezShareLink', 'EZSHARE_LINK'])),
      ezshareId: this.toText(this.read(row, ['ezshareId', 'ezShareId', 'EZSHARE_ID'])),
      createdAt: this.toText(this.read(row, ['dateCreated', 'createdAt', 'DATE_CREATED'])),
      createdBy: this.toText(this.read(row, ['createdBy', 'CREATED_BY'])),
    };
  }

  private normalizeInvoiceDocumentRow(row: Record<string, unknown>): InvoiceDocument {
    return {
      id: this.toNumber(this.read(row, ['id', 'ID'])),
      description: this.toText(this.read(row, ['description', 'DESCRIPTION'])),
      type: this.toText(this.read(row, ['type', 'TYPE'])),
      fileName: this.toText(this.read(row, ['fileName', 'FILE_NAME'])),
      ezshareLink: this.toText(this.read(row, ['ezshareLink', 'ezShareLink', 'EZSHARE_LINK'])),
      ezshareId: this.toText(this.read(row, ['ezshareId', 'ezShareId', 'EZSHARE_ID'])),
      isCurrent: this.toText(this.read(row, ['isCurrent', 'IS_CURRENT'])).toUpperCase() === 'Y',
      createdAt: this.toText(this.read(row, ['dateCreated', 'createdAt', 'DATE_CREATED'])),
      createdBy: this.toText(this.read(row, ['createdBy', 'CREATED_BY'])),
    };
  }

  private normalizeLineRow(
    row: Record<string, unknown>,
    conceptById: Map<number, string>,
    bondDocById: Map<number, string>,
  ): InvoiceLine {
    const invConceptId = this.toNumber(this.read(row, ['invConceptId', 'invoiceConceptId', 'conceptId', 'INV_CONCEPT_ID']));
    const bondDocumentationId = this.toNullableNumber(this.read(row, ['bondDocumentationId', 'BOND_DOCUMENTATION_ID']));

    return {
      id: this.toNullableNumber(this.read(row, ['id', 'ID'])) ?? undefined,
      lineNumber: this.toNumber(this.read(row, ['lineNumber', 'LINE_NUMBER'])),
      invConceptId,
      conceptCode: this.toText(this.read(row, ['conceptCode', 'CONCEPT_CODE'])),
      conceptName: this.toText(this.read(row, ['conceptName', 'CONCEPT_NAME', 'invConceptName']))
        || conceptById.get(invConceptId)
        || '',
      linkTypeCode: this.toLinkTypeCode(this.toText(this.read(row, ['linkTypeCode', 'LINK_TYPE_CODE']))),
      tradeId: this.toText(this.read(row, ['tradeId', 'TRADE_ID'])),
      dealId: this.toText(this.read(row, ['dealId', 'DEAL_ID'])),
      securityId: this.toText(this.read(row, ['securityId', 'SECURITY_ID'])),
      bondDocumentationId,
      bondDocumentationName: this.toText(this.read(row, ['bondDocumentationName', 'BOND_DOCUMENTATION_NAME']))
        || (bondDocumentationId ? (bondDocById.get(bondDocumentationId) ?? '') : ''),
      tradeCcy: this.toText(this.read(row, ['tradeCcy', 'currency', 'TRADE_CCY'])),
      lineAmount: this.toNumber(this.read(row, ['lineAmount', 'LINE_AMOUNT'])),
      detail: this.toText(this.read(row, ['detail', 'DETAIL'])),
    };
  }

  private normalizeInvoiceSummaryRow(row: Record<string, unknown>): InvoiceSummary {
    const sources = [
      row,
      this.toRecord(row['data']),
      this.toRecord(row['item']),
      this.toRecord(row['invoice']),
      this.toRecord(row['header']),
      this.toRecord(row['result']),
    ];
    const lines = this.extractRows(this.readFromSources(sources, ['lines']))
      .map((line) => this.normalizeLineRow(line, new Map(), new Map()));
    const conceptIds = [...new Set(lines.map((line) => line.invConceptId).filter((id) => id > 0))];
    const conceptNames = [...new Set(lines.map((line) => line.conceptName).filter((name) => !!name))];
    const linkTypeCode = this.toLinkTypeCode(
      this.toText(this.readFromSources(sources, ['linkTypeCode', 'LINK_TYPE_CODE'])),
    );
    const programLine = lines.find((line) => line.bondDocumentationId !== null);

    return this.toSummary({
      id: this.toNumber(this.readFromSources(sources, ['id', 'ID'])),
      vendorId: this.toNumber(this.readFromSources(sources, ['vendorId', 'VENDOR_ID'])),
      vendorName: this.toText(this.readFromSources(sources, ['vendorName', 'VENDOR_NAME'])),
      issueDate: this.toText(this.readFromSources(sources, ['issueDate', 'ISSUE_DATE'])),
      invoiceConceptIds: conceptIds,
      invoiceConceptNames: conceptNames,
      linkType: this.toUiLinkType(linkTypeCode),
      linkTypeCode,
      linkedProgram: programLine?.bondDocumentationName || null,
      linkedBondDocumentationId: programLine?.bondDocumentationId ?? null,
      invoiceNumber: this.toText(this.readFromSources(sources, ['invoiceNumber', 'INVOICE_NUMBER'])),
      serviceDateStart: this.toNullableText(
        this.readFromSources(sources, ['serviceStartDate', 'serviceDateStart', 'SERVICE_START_DATE']),
      ),
      serviceDateEnd: this.toNullableText(
        this.readFromSources(sources, ['serviceEndDate', 'serviceDateEnd', 'SERVICE_END_DATE']),
      ),
      currency: this.toText(this.readFromSources(sources, ['invoiceCcy', 'currency', 'INVOICE_CCY'])),
      frequency: this.toText(this.readFromSources(sources, ['invoiceFreqName', 'frequency', 'INVOICE_FREQ'])),
      frequencyCode: this.toText(
        this.readFromSources(sources, ['invoiceFreq', 'frequencyCode', 'INVOICE_FREQ']),
      ),
      amount: this.toNumber(this.readFromSources(sources, ['totalAmount', 'amount', 'TOTAL_AMOUNT'])),
      createdAt: this.toText(
        this.readFromSources(sources, ['createdDate', 'dateCreated', 'createdAt', 'DATE_CREATED', 'createdOn', 'CREATED_ON']),
      ),
      createdBy: this.toText(this.readFromSources(sources, ['createdBy', 'CREATED_BY'])),
      lastModifiedBy: this.toText(
        this.readFromSources(sources, ['lastModifiedBy', 'modifiedBy', 'MODIFIED_BY']),
      ),
      lines,
      documents: [],
      warnings: this.normalizeWarnings(this.readFromSources(sources, ['warnings'])),
    });
  }

  private normalizeWarnings(value: unknown): InvoiceWarning[] {
    return this.extractRows(value).map((row) => ({
      code: this.toText(this.read(row, ['code', 'CODE'])),
      message: this.toText(this.read(row, ['message', 'MESSAGE'])),
    })).filter((warning) => !!warning.code || !!warning.message);
  }

  private normalizeBondTrade(row: Record<string, unknown>): BondTradeReference {
    return {
      tradeId: this.toText(this.read(row, ['tradeId', 'TRADE_ID'])),
      dealId: this.toText(this.read(row, ['dealId', 'DEAL_ID'])),
      securityId: this.toText(this.read(row, ['securityId', 'SECURITY_ID'])),
      program: this.toText(this.read(row, ['marketType', 'structureId', 'program', 'MARKET_TYPE', 'STRUCTURE_ID'])),
      marketType: this.toText(this.read(row, ['marketType', 'MARKET_TYPE', 'mktType', 'MKT_TYPE'])),
      tradeDate: this.toText(this.read(row, ['tradeDate', 'TRADE_DATE'])),
      settlementDate: this.toText(this.read(row, ['settlementDate', 'SETTLEMENT_DATE'])),
      maturityDate: this.toText(this.read(row, ['maturityDate', 'MaturityDate', 'MATURITY_DATE'])),
      cusip: this.toText(this.read(row, ['cusip', 'CUSIP'])),
      isin: this.toText(this.read(row, ['isin', 'ISIN'])),
      tranche: this.toText(this.read(row, ['tranche', 'TRANCHE'])),
      seriesNumber: this.toText(this.read(row, [
        'bondPricingSupplement',
        'BondPricingSupplement',
        'BOND_PRICING_SUPPLEMENT',
        'seriesNumber',
      ])),
      currency: this.toText(this.read(row, ['currency', 'CURRENCY'])),
      notional: this.toNumber(this.read(row, ['notional', 'NOTIONAL'])),
    };
  }

  private normalizeInvoiceRelatedRecord(
    row: Record<string, unknown>,
    fallbackLinkTypeCode: InvoiceRelatedRecord['linkTypeCode'],
  ): InvoiceRelatedRecord {
    const rawLinkTypeCode = this.toLinkTypeCode(this.toText(this.read(row, ['linkTypeCode', 'LINK_TYPE_CODE'])));

    return {
      linkTypeCode: rawLinkTypeCode === 'NONE' ? fallbackLinkTypeCode : rawLinkTypeCode,
      tradeId: this.toText(this.read(row, ['tradeId', 'TRADE_ID'])),
      dealId: this.toText(this.read(row, ['dealId', 'DEAL_ID'])),
      securityId: this.toText(this.read(row, ['securityId', 'SECURITY_ID'])),
      bondDocumentationId: this.toNullableNumber(this.read(row, ['bondDocumentationId', 'BOND_DOCUMENTATION_ID'])),
      bondDocumentationName: this.toText(this.read(row, ['bondDocumentationName', 'programName', 'BOND_DOCUMENTATION_NAME'])),
      seriesNumber: this.toText(this.read(row, [
        'seriesNumber',
        'bondPricingSupplement',
        'BondPricingSupplement',
        'BOND_PRICING_SUPPLEMENT',
      ])),
      marketType: this.toText(this.read(row, ['marketType', 'MARKET_TYPE', 'mktType', 'MKT_TYPE'])),
      securityType: this.toText(this.read(row, ['securityType', 'SECURITY_TYPE'])),
      currency: this.toText(this.read(row, ['currency', 'tradeCcy', 'CURRENCY', 'TRADE_CCY'])),
      tradeDate: this.toText(this.read(row, ['tradeDate', 'TRADE_DATE'])),
      settlementDate: this.toText(this.read(row, ['settlementDate', 'SETTLEMENT_DATE'])),
      maturityDate: this.toText(this.read(row, ['maturityDate', 'MATURITY_DATE'])),
      notional: this.toNumber(this.read(row, ['notional', 'NOTIONAL'])),
      cusip: this.toText(this.read(row, ['cusip', 'CUSIP'])),
      isin: this.toText(this.read(row, ['isin', 'ISIN'])),
      tranche: this.toText(this.read(row, ['tranche', 'TRANCHE'])),
    };
  }

  private toSummary(invoice: InvoiceRecord): InvoiceSummary {
    const lineTotal = invoice.lines.reduce((sum, line) => sum + line.lineAmount, 0);
    return {
      ...invoice,
      lines: invoice.lines.map((line) => ({ ...line })),
      documents: invoice.documents.map((document) => ({ ...document })),
      warnings: invoice.warnings.map((warning) => ({ ...warning })),
      lineTotal,
    };
  }

  private buildInvoiceFallbackMap(rows: Record<string, unknown>[]): Map<number, {
    createdAt: string;
    createdBy: string;
    lastModifiedBy: string;
  }> {
    const mapById = new Map<number, { createdAt: string; createdBy: string; lastModifiedBy: string }>();

    for (const row of rows) {
      const id = this.extractInvoiceId(row);
      if (id <= 0) {
        continue;
      }

      mapById.set(id, {
        createdAt: this.toText(this.read(row, ['createdDate', 'dateCreated', 'createdAt', 'DATE_CREATED', 'createdOn', 'CREATED_ON'])),
        createdBy: this.toText(this.read(row, ['createdBy', 'CREATED_BY'])),
        lastModifiedBy: this.toText(this.read(row, ['lastModifiedBy', 'modifiedBy', 'MODIFIED_BY'])),
      });
    }

    return mapById;
  }

  private applyInvoiceFallback(
    invoice: InvoiceSummary,
    fallback: { createdAt: string; createdBy: string; lastModifiedBy: string } | undefined,
  ): InvoiceSummary {
    if (!fallback) {
      return invoice;
    }

    return {
      ...invoice,
      createdAt: invoice.createdAt || fallback.createdAt,
      createdBy: invoice.createdBy || fallback.createdBy,
      lastModifiedBy: invoice.lastModifiedBy || fallback.lastModifiedBy,
    };
  }

  private normalizeReferenceOptions(response: unknown): Array<{ code: string; name: string }> {
    const primitiveArray = this.extractPrimitiveArray(response);
    if (primitiveArray.length > 0) {
      return primitiveArray.map((item) => {
        const value = this.toText(item).toUpperCase();
        return { code: value, name: value };
      }).filter((item) => !!item.code);
    }

    const rows = this.extractRows(response);
    if (rows.length > 0) {
      return rows.map((row) => ({
        code: this.toText(this.read(row, ['code', 'value', 'id'])).toUpperCase(),
        name: this.toText(this.read(row, ['name', 'label', 'code'])),
      })).filter((item) => !!item.code);
    }

    return [];
  }

  private extractPrimitiveArray(response: unknown): unknown[] {
    if (Array.isArray(response)) {
      return response.every((item) => item === null || typeof item !== 'object') ? response : [];
    }

    const record = this.toRecord(response);
    const data = record['data'];
    const items = record['items'];

    if (Array.isArray(data) && data.every((item) => item === null || typeof item !== 'object')) {
      return data;
    }

    if (Array.isArray(items) && items.every((item) => item === null || typeof item !== 'object')) {
      return items;
    }

    return [];
  }

  private frequencyNameFromCode(code: string): string {
    switch (code) {
      case 'ONE_TIME':
      case 'ONE PAYMENT':
        return 'One payment';
      case 'MONTHLY':
        return 'Monthly';
      case 'YEARLY':
        return 'Yearly';
      default:
        return code;
    }
  }

  private linkTypeNameFromCode(code: string): string {
    switch (code) {
      case 'NONE':
        return 'Generic';
      case 'DEAL':
        return 'Deal ID';
      case 'SECURITY':
        return 'Security ID';
      case 'BOND_DOCUMENTATION':
        return 'Program';
      default:
        return code;
    }
  }

  private toApiLinkTypeCode(linkType: InvoiceLinkType): InvoiceLinkTypeCode {
    switch (linkType) {
      case 'Deal ID':
        return 'DEAL';
      case 'Security ID':
        return 'SECURITY';
      case 'Program':
        return 'BOND_DOCUMENTATION';
      default:
        return 'NONE';
    }
  }

  private toLinkTypeCode(code: string): InvoiceLinkTypeCode {
    switch (code.toUpperCase()) {
      case 'DEAL':
        return 'DEAL';
      case 'SECURITY':
        return 'SECURITY';
      case 'BOND_DOCUMENTATION':
        return 'BOND_DOCUMENTATION';
      default:
        return 'NONE';
    }
  }

  private toUiLinkType(code: string): InvoiceLinkType {
    switch (code.toUpperCase()) {
      case 'DEAL':
        return 'Deal ID';
      case 'SECURITY':
        return 'Security ID';
      case 'BOND_DOCUMENTATION':
        return 'Program';
      default:
        return 'Generic';
    }
  }

  private extractCreatedId(response: unknown): number {
    if (typeof response === 'number' && Number.isFinite(response)) {
      return response;
    }

    const record = this.toRecord(response);
    return this.toNumber(this.read(record, ['id', 'ID', 'invoiceId', 'vendorId', 'data.id']));
  }

  private extractInvoiceId(row: Record<string, unknown>): number {
    return this.toNumber(this.read(row, ['id', 'ID', 'invoiceId', 'INVOICE_ID']));
  }

  private extractRows(response: unknown): Record<string, unknown>[] {
    if (Array.isArray(response)) {
      return response.map((item) => this.toRecord(item));
    }

    const record = this.toRecord(response);
    const candidates = [record['items'], record['data'], record['rows'], record['result']];

    for (const candidate of candidates) {
      if (Array.isArray(candidate)) {
        return candidate.map((item) => this.toRecord(item));
      }
    }

    if (Object.keys(record).length > 0) {
      return [record];
    }

    return [];
  }

  private read(record: Record<string, unknown>, keys: string[]): unknown {
    for (const key of keys) {
      if (key.includes('.')) {
        const nested = key.split('.').reduce<unknown>((value, part) => {
          if (!value || typeof value !== 'object') {
            return undefined;
          }
          return (value as Record<string, unknown>)[part];
        }, record);

        if (nested !== undefined) {
          return nested;
        }
        continue;
      }

      if (Object.prototype.hasOwnProperty.call(record, key)) {
        return record[key];
      }
    }

    return this.readByNormalizedKey(record, keys);
  }

  private readByNormalizedKey(record: Record<string, unknown>, keys: string[]): unknown {
    const wanted = new Set(keys.filter((key) => !key.includes('.')).map((key) => this.normalizeKey(key)));
    if (wanted.size === 0) {
      return undefined;
    }

    for (const [key, value] of Object.entries(record)) {
      if (wanted.has(this.normalizeKey(key)) && value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return undefined;
  }

  private normalizeKey(key: string): string {
    return key.replace(/[^a-z0-9]/gi, '').toLowerCase();
  }

  private readFromSources(sources: Record<string, unknown>[], keys: string[]): unknown {
    for (const source of sources) {
      const value = this.read(source, keys);
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return undefined;
  }

  private toRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? value as Record<string, unknown> : {};
  }

  private toText(value: unknown): string {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value);
    }

    return '';
  }

  private toNullableText(value: unknown): string | null {
    const text = this.toText(value);
    return text ? text : null;
  }

  private toNumber(value: unknown): number {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : 0;
    }

    return 0;
  }

  private toNullableNumber(value: unknown): number | null {
    const parsed = this.toNumber(value);
    return parsed > 0 ? parsed : null;
  }

  private toEpoch(value: string): number {
    const epoch = Date.parse(value);
    return Number.isFinite(epoch) ? epoch : 0;
  }

  private normalizeText(value: string): string {
    return value.trim();
  }

  private resolveModifiedBy(): string {
    const active = this.msalService.instance.getActiveAccount();
    if (active?.username) {
      return active.username;
    }

    const first = this.msalService.instance.getAllAccounts()[0];
    if (first?.username) {
      return first.username;
    }

    return 'unknown.user';
  }
}
